import { useEffect, useRef, useState } from 'react'

import { QuestionCircleOutlined } from '@ant-design/icons'
import { useEventListener } from 'ahooks'
import { Button, Flex, Image, Popover, Typography } from 'antd'

import { loginMemberByOpenCodeApi } from '@eastcoal/api/auth/memberLogin/loginMemberByOpenCode'

import { parseUrl } from '@eastcoal/common/utils/querystring'

import { WxOAuth2Props } from './interface'

const WX_CONTAINER_ID = 'wx_oauth2_container'
const WX_LOGIN_SRC =
  'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js'
// 如果使用阿里云 OSS 服务需要自定义域名，不然会强制触发下载行为
const WX_OPEN_OAUTH2_REDIRECT_PATH =
  'https://i.eastcoal.cn/pages/open-oauth2-redirect-page.html'
const CHROME_SETTING_SNIPASTE =
  'https://eastcoal-public.oss-cn-shanghai.aliyuncs.com/frontend/auth/chrome_settings_snipaste.png'
const CHROME_PERMISSION_LOCAL_NETWORK_ACCESS =
  'https://eastcoal-public.oss-cn-shanghai.aliyuncs.com/frontend/auth/chrome_permissions_local_network_access.png'

/**
 * docs: https://developers.weixin.qq.com/doc/oplatform/Website_App/WeChat_Login/Wechat_Login.html
 */
export const WxOAuth2 = ({ className, style, onLogin }: WxOAuth2Props) => {
  const iframeContainerRef = useRef<HTMLDivElement>(null)
  const [isLocalNetworkAccessDenied, setIsLocalNetworkAccessDenied] =
    useState(false)
  const [step, setStep] = useState<'init' | 'processing' | 'done'>('init')

  const doLogin = async (redirectUrl: string) => {
    const { query } = parseUrl(redirectUrl)
    try {
      setStep('processing')
      await loginMemberByOpenCodeApi(
        { code: query.code as string },
        { silence: true }
      )
      onLogin?.()
    } catch (error) {
      onLogin?.({ error })
    } finally {
      setStep('done')
    }
  }

  useEffect(() => {
    const script = document.createElement('script')
    script.src = WX_LOGIN_SRC
    script.async = true
    script.onload = () => {
      new (window as any).WxLogin({
        self_redirect: true,
        id: WX_CONTAINER_ID,
        appid: 'wx00f45b4cb7d7a75c',
        scope: 'snsapi_login',
        redirect_uri: encodeURIComponent(WX_OPEN_OAUTH2_REDIRECT_PATH),
        style: 'black',
        stylelite: 1,
        fast_login: 1,
        onReady: () => {
          // 查找容器下第一个 iframe 元素，调整其样式以适应容器
          const container = iframeContainerRef.current
          if (container) {
            const iframe = container.querySelector('iframe')
            if (iframe) {
              iframe.style.height = 'auto'
            }
          }
        }
      })
    }
    document.head.appendChild(script)
    return () => {
      document.head.removeChild(script)
    }
  }, [isLocalNetworkAccessDenied])

  useEffect(() => {
    let timer: number | undefined
    const detectLocalNetworkAccess = () => {
      ;(navigator as any).permissions
        ?.query({ name: 'local-network-access' })
        .then((status: any) => {
          if (status?.state !== 'granted') {
            timer = window.setTimeout(detectLocalNetworkAccess, 1000)
          }
          setIsLocalNetworkAccessDenied(status?.state === 'denied')
        })
        .catch(() => {})
    }
    detectLocalNetworkAccess()
    return () => window.clearTimeout(timer)
  }, [])

  useEventListener(
    'message',
    event => {
      if (event.data?.type !== 'oauth2-redirect-message') {
        return
      }
      const redirectUrl = event.data?.redirectUrl
      if (!redirectUrl) {
        console.error('微信登录回调地址缺失')
        onLogin?.({
          error: new Error('微信登录异常，请尝试其他方式登录')
        })
        return
      }
      doLogin(redirectUrl)
    },
    {
      target: window
    }
  )

  const wxQrcodeNode = step === 'init' && (
    <>
      <div
        ref={iframeContainerRef}
        id={WX_CONTAINER_ID}
        style={{
          height: 160,
          textAlign: 'center'
        }}
      />
      {isLocalNetworkAccessDenied && (
        <div style={{ marginTop: 8 }}>
          <Typography.Text>
            点击
            <Button
              size='small'
              type='link'
              onClick={async () => {
                const isEdge = navigator.userAgent.includes('Edg/')
                const base = isEdge
                  ? 'edge://settings/content/siteDetails?site='
                  : 'chrome://settings/content/siteDetails?site='
                const url = `${base}${encodeURIComponent(window.location.origin)}`
                try {
                  await navigator.clipboard.writeText(url)
                } catch {
                  // do nothing
                }
              }}
            >
              设置链接
            </Button>
            并粘贴到浏览器地址栏打开
            <Popover
              content={
                <div>
                  1. 请在打开的页面中找到
                  <Typography.Text code>本地网络访问</Typography.Text>
                  选项，并启用它。
                  <br />
                  <br />
                  <Image src={CHROME_SETTING_SNIPASTE} width={300} />
                  <br />
                  2. 在登录页面授权弹窗中，选择“允许”。
                  <br />
                  <br />
                  <Image
                    src={CHROME_PERMISSION_LOCAL_NETWORK_ACCESS}
                    width={300}
                  />
                </div>
              }
            >
              <QuestionCircleOutlined />
            </Popover>
            ，开启 <Typography.Text code>本地网络访问</Typography.Text>
            权限以使用微信快捷登录。
          </Typography.Text>
        </div>
      )}
    </>
  )

  const loadingNode = step !== 'init' && (
    <Flex align='center' justify='center' style={{ height: 160 }}>
      <Button loading type='link'>
        正在登录，请稍候...
      </Button>
    </Flex>
  )

  return (
    <div className={className} style={style}>
      {wxQrcodeNode}
      {loadingNode}
    </div>
  )
}
